/**
 * The authenticated app shell: a left sidebar carrying the brand, the section nav, and the Account
 * switcher; a top bar carrying the signed-in person; the routed page below via react-router's
 * <Outlet />; and a footer.
 *
 * ## Why the switcher is in the sidebar and the person is in the top bar
 *
 * They answer different questions and were previously sitting side by side answering neither clearly.
 * *Which workspace am I acting on* is navigation context — it changes what every page below it shows, so
 * it belongs in the rail with the nav it qualifies, and only when there is a choice to make: with a single
 * Account the switcher renders nothing at all, and the rail ends at the nav.
 *
 * *Who am I* is not context for anything on the page, so it collapses into a glyph in the corner (see
 * {@link UserPanel}) — and every identity affordance now sits behind it, sign-out included. That control
 * used to stand in this bar on the argument that ending a session is the one thing a person may want in a
 * hurry; it was asked directly and decided the other way, in favour of a single identity surface. The bar
 * is therefore chrome for one thing: the glyph. The rail no longer restates the Account ID or the role,
 * because the panel says both.
 *
 * The nav is not role-gated, and the reason differs by entry. Checks, Check drafts, Alert rules, and
 * Members all point at a list a viewer may read (Req 9.7 makes the drafts list visible to a viewer and
 * withholds only its actions; Members likewise, because knowing who can see the Account's data is not
 * privileged — user-and-data-management Req 6), so gating those links would hide a page the user is
 * allowed to see. **API keys and Inference are the exceptions, and they stay ungated anyway**:
 * `api-key:read` and every `inference-config:*` action are granted to admin alone, so a viewer or editor
 * genuinely cannot read either page — but each page answers them with an explanation that the surface
 * belongs to an account administrator (terraform-provider Req 11.2; customer-managed-inference
 * Req 13.5), and a hidden link would leave them with no way to find that out. A visible entry leading
 * to an explanation beats an absence leading to a guess. Write affordances are gated inside the pages.
 *
 * This module and {@link UserPanel} are written in raw Tailwind utilities, as is the rail divider that
 * {@link AccountSwitcher} carries with its own content. Everything else styles itself through the semantic
 * classes in `styles.css`; the shell has no such vocabulary to preserve, because it is new markup.
 */

import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { AccountSwitcher } from './AccountSwitcher';
import { UserPanel } from './UserPanel';

interface NavItem {
  readonly to: string;
  readonly label: string;
  /**
   * Path prefixes that should also light this entry up.
   *
   * Only the Checks entry needs them, for two reasons. It is the index route, so its `to` is `/`, which
   * `NavLink` matches exactly and nothing else. And three of its subtrees have no entry of their own:
   * `/checks/…`, plus `/results/…` and `/traces/…`, which are reachable only by drilling into a check.
   * Without these the rail would go blank on the pages a reader spends the most time on.
   *
   * `/profile` is deliberately absent: it is reached from the user panel in the top bar rather than from
   * the nav, because it describes the person and the nav lists the Account's monitoring surfaces.
   */
  readonly alsoActiveUnder?: readonly string[];
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'Checks', alsoActiveUnder: ['/checks', '/results', '/traces'] },
  { to: '/check-drafts', label: 'Check drafts' },
  { to: '/alert-rules', label: 'Alert rules' },
  { to: '/members', label: 'Members' },
  // Last, beside Members: all three are Account administration rather than monitoring. See the module
  // doc on why the two admin-only pages are in the nav despite a viewer being unable to read either.
  { to: '/api-keys', label: 'API keys' },
  { to: '/inference', label: 'Inference' },
];

// `no-underline` needs no `hover:` twin: it is a utility, and the utilities layer outranks the base
// `a:hover` underline in every state.
const NAV_LINK_BASE =
  'block rounded-control px-2.5 py-1.5 text-[0.8125rem] no-underline transition-colors';

function navLinkClass(active: boolean): string {
  return active
    ? `${NAV_LINK_BASE} bg-accent/12 font-medium text-ink`
    : `${NAV_LINK_BASE} text-muted hover:bg-white/5 hover:text-ink`;
}

function SidebarNavLink({ item }: { item: NavItem }): JSX.Element {
  const { pathname } = useLocation();
  const underPrefix =
    item.alsoActiveUnder?.some((prefix) => pathname.startsWith(prefix)) ?? false;

  /*
   * The prefix case cannot be expressed as a `NavLink`, and the reason is worth stating so nobody
   * "simplifies" it back: `NavLink` applies `aria-current` only when its OWN match succeeds — a value
   * passed in is the value it uses *if* active, not a way to force the attribute — and `to="/"` matches
   * the path `/` and nothing else regardless of `end`. So on `/checks/chk-1` the Checks entry can never
   * be a matching `NavLink`, and rendering one would leave the rail claiming the reader is nowhere.
   * A plain `Link` that states the current entry directly is the honest way to say it.
   */
  if (underPrefix) {
    return (
      <Link to={item.to} aria-current="page" className={navLinkClass(true)}>
        {item.label}
      </Link>
    );
  }

  return (
    <NavLink
      to={item.to}
      // Exact for the index route, so `/` cannot claim a path it only prefixes.
      end={item.to === '/'}
      className={({ isActive }) => navLinkClass(isActive)}
    >
      {item.label}
    </NavLink>
  );
}

export function Layout(): JSX.Element {
  return (
    <div className="grid min-h-screen bg-canvas md:grid-cols-[var(--spacing-rail)_minmax(0,1fr)]">
      {/* The rail. Sticky and full-height from `md` up; above `md` it collapses to a block at the top of
          the document, because a 248px column on a phone leaves nothing for the page. */}
      <aside className="flex flex-col gap-6 border-b border-hairline bg-surface px-4 py-5 md:sticky md:top-0 md:h-screen md:overflow-y-auto md:border-b-0 md:border-r">
        <Link
          to="/"
          className="px-1 text-[1.0625rem] font-semibold tracking-tight text-ink no-underline"
        >
          Soroush
        </Link>

        <nav className="flex flex-col gap-0.5" aria-label="Main">
          {NAV_ITEMS.map((item) => (
            <SidebarNavLink key={item.to} item={item} />
          ))}
        </nav>

        {/*
          The Account switcher, directly below the nav it qualifies — when there is one. It renders
          nothing for a User with a single Account, so it owns its own hairline divider rather than being
          wrapped in one here: a `<div className="border-t …">` around a component that returns `null`
          shows as a stray rule with nothing under it, and the only honest way for this rail to avoid that
          is to not decide the question. See the note in {@link AccountSwitcher} on why the divider
          travelled with the content rather than the predicate travelling up here.
        */}
        <AccountSwitcher />
      </aside>

      <div className="flex min-w-0 flex-col">
        {/* One control, right-aligned. `justify-end` without a `gap`: the gap existed to separate the
            glyph from the sign-out button that used to stand beside it. */}
        <header className="sticky top-0 z-20 flex h-14 items-center justify-end border-b border-hairline bg-canvas/85 px-6 backdrop-blur-md">
          <UserPanel />
        </header>

        <main className="mx-auto w-full max-w-[60rem] flex-1 px-6 py-8">
          <Outlet />
        </main>

        {/* Computed at render, not baked in: a hardcoded year is wrong from the first of January. */}
        <footer className="border-t border-hairline px-6 py-5 text-center text-xs text-muted">
          Built by Dana AI © {new Date().getFullYear()} All rights reserved
        </footer>
      </div>
    </div>
  );
}
